import classNames from 'classnames';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { WinterLogo } from '@sorare/core/src/atoms/icons/WinterLogo';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import {
  type SupportedCardSet,
  isSupportedCardSet,
} from '@sorare/core/src/lib/cardSets';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useGalleryRarityLayoutContext } from 'hooks/my-cards/useGalleryRarityLayoutContext';

import { CollectionSetBackground } from '../CollectionSetBackground';

const Root = styled.div`
  padding: var(--double-unit);
  width: 100%;
  @media ${laptopAndAbove} {
    width: unset;
    max-width: 1000px;
  }
`;

const Item = styled(ButtonBase)`
  padding: var(--double-unit) var(--quadruple-unit);
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 1px solid var(--c-nd-100);
  border-radius: var(--double-unit);
  overflow: hidden;
  &:hover,
  &:focus,
  &.active {
    border-color: var(--c-blue-400);
  }
  @media ${laptopAndAbove} {
    padding: var(--quadruple-unit) calc(8 * var(--unit));
  }
`;

const IconWrapper = styled.div`
  & img {
    object-fit: contain;
  }
`;

const BackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
type Props = {
  cardSetSlug: string;
  path: (cardSetSlug: string) => string;
};

const SET_ASSETS: Record<
  SupportedCardSet,
  { Icon: React.FC<{ cardSet: { name: string } }> }
> = {
  winter: {
    Icon: () => (
      <WinterLogo
        style={{
          height: 'var(--double-unit)',
        }}
      />
    ),
  },
  'common-base-set': {
    Icon: ({ cardSet }: { cardSet: { name: string } }) => (
      <BrandXS className="text-center">{cardSet.name}</BrandXS>
    ),
  },
};

export const CollectionSetSwitcher = ({ cardSetSlug, path }: Props) => {
  const { cardSets } = useGalleryRarityLayoutContext();

  const seriesNav = useSeriesNav();
  if (
    cardSets.length <= 1 ||
    // ideally backend should tell only returns active card sets
    // so we don't need to check this here
    seriesNav
  ) {
    return null;
  }
  return (
    <Root className="FilterWidget visible">
      <Scrollable itemToDisplay={2} itemGap={0.5} overhang={0}>
        {cardSets
          .filter(cardSet => isSupportedCardSet(cardSet.slug))
          .map(cardSet => {
            const currentCardSetSlug = cardSet.slug as SupportedCardSet;
            const { Icon } = SET_ASSETS[currentCardSetSlug];
            return (
              <Item
                key={currentCardSetSlug}
                to={path(currentCardSetSlug)}
                replace
                className={classNames({
                  active: currentCardSetSlug === cardSetSlug,
                })}
              >
                <BackgroundWrapper>
                  <CollectionSetBackground set={currentCardSetSlug} />
                </BackgroundWrapper>
                <IconWrapper>
                  {!!Icon && <Icon cardSet={cardSet} />}
                </IconWrapper>
              </Item>
            );
          })}
      </Scrollable>
    </Root>
  );
};
